import {
  makeStyles,
  tokens,
  Button,
  Avatar,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  MenuDivider,
  Text,
  Toolbar,
  ToolbarButton,
} from '@fluentui/react-components';
import {
  VehicleCarParkingRegular,
  PersonRegular,
  SignOutRegular,
  CalendarAgendaRegular,
  NavigationRegular,
  CalendarSyncRegular,
} from '@fluentui/react-icons';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import { useIsMobile } from '../../hooks/useMediaQuery';
import MyReservationsDialog from '../Reservations/MyReservationsDialog';
import RecurringRulesDialog from '../Reservations/RecurringRulesDialog';
import LanguageSwitcher from './LanguageSwitcher';

const useStyles = makeStyles({
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalL}`,
    backgroundColor: tokens.colorBrandBackground,
    color: tokens.colorNeutralForegroundOnBrand,
    boxShadow: tokens.shadow4,
    '@media (max-width: 599px)': {
      padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    },
  },
  logo: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  logoIcon: {
    fontSize: '28px',
    '@media (max-width: 599px)': {
      fontSize: '24px',
    },
  },
  title: {
    fontSize: tokens.fontSizeBase500,
    fontWeight: tokens.fontWeightSemibold,
    fontFamily: "'Jost', 'Segoe UI', sans-serif",
    color: tokens.colorNeutralForegroundOnBrand,
    '@media (max-width: 599px)': {
      fontSize: tokens.fontSizeBase400,
    },
  },
  titleShort: {
    display: 'none',
    '@media (max-width: 599px)': {
      display: 'inline',
    },
  },
  titleFull: {
    '@media (max-width: 599px)': {
      display: 'none',
    },
  },
  toolbar: {
    gap: tokens.spacingHorizontalS,
    '@media (max-width: 599px)': {
      display: 'none',
    },
  },
  rightSection: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    '@media (max-width: 599px)': {
      gap: tokens.spacingHorizontalS,
    },
  },
  userName: {
    color: tokens.colorNeutralForegroundOnBrand,
    '@media (max-width: 599px)': {
      display: 'none',
    },
  },
  mobileMenuButton: {
    display: 'none',
    color: tokens.colorNeutralForegroundOnBrand,
    '@media (max-width: 599px)': {
      display: 'flex',
    },
  },
  menuIcon: {
    color: tokens.colorNeutralForegroundOnBrand,
  },
});

export default function Header() {
  const styles = useStyles();
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const isMobile = useIsMobile();
  const [showMyReservations, setShowMyReservations] = useState(false);
  const [showRecurringRules, setShowRecurringRules] = useState(false);

  return (
    <>
      <header className={styles.header}>
        <div className={styles.logo}>
          <VehicleCarParkingRegular className={styles.logoIcon} />
          <Text className={styles.title}>
            <span className={styles.titleFull}>{t('header.title')}</span>
            <span className={styles.titleShort}>{t('header.titleShort')}</span>
          </Text>
        </div>

        {/* Desktop Toolbar */}
        <Toolbar className={styles.toolbar}>
          <ToolbarButton
            appearance="transparent"
            icon={<CalendarAgendaRegular />}
            onClick={() => setShowMyReservations(true)}
            style={{ color: tokens.colorNeutralForegroundOnBrand }}
          >
            {t('header.myReservations')}
          </ToolbarButton>
          <ToolbarButton
            appearance="transparent"
            icon={<CalendarSyncRegular />}
            onClick={() => setShowRecurringRules(true)}
            style={{ color: tokens.colorNeutralForegroundOnBrand }}
          >
            {t('header.recurring')}
          </ToolbarButton>
        </Toolbar>

        <div className={styles.rightSection}>
          {/* Language Switcher */}
          <LanguageSwitcher />

          {/* Mobile Menu */}
          <Menu>
            <MenuTrigger disableButtonEnhancement>
              <Button
                className={styles.mobileMenuButton}
                appearance="transparent"
                icon={<NavigationRegular className={styles.menuIcon} />}
                size="large"
              />
            </MenuTrigger>
            <MenuPopover>
              <MenuList>
                <MenuItem
                  icon={<CalendarAgendaRegular />}
                  onClick={() => setShowMyReservations(true)}
                >
                  {t('header.myReservations')}
                </MenuItem>
                <MenuItem
                  icon={<CalendarSyncRegular />}
                  onClick={() => setShowRecurringRules(true)}
                >
                  {t('header.recurring')}
                </MenuItem>
                <MenuDivider />
                <MenuItem icon={<SignOutRegular />} onClick={logout}>
                  {t('header.logout')}
                </MenuItem>
              </MenuList>
            </MenuPopover>
          </Menu>

          {/* User Menu (Desktop) / Avatar only (Mobile) */}
          <Menu>
            <MenuTrigger disableButtonEnhancement>
              <Button
                appearance="transparent"
                icon={
                  <Avatar
                    name={user?.name}
                    size={isMobile ? 28 : 32}
                    color="brand"
                    icon={<PersonRegular />}
                  />
                }
              >
                <Text className={styles.userName}>{user?.name}</Text>
              </Button>
            </MenuTrigger>
            <MenuPopover>
              <MenuList>
                {isMobile && (
                  <>
                    <MenuItem disabled>
                      <Text weight="semibold">{user?.name}</Text>
                    </MenuItem>
                    <MenuDivider />
                  </>
                )}
                <MenuItem icon={<SignOutRegular />} onClick={logout}>
                  {t('header.logout')}
                </MenuItem>
              </MenuList>
            </MenuPopover>
          </Menu>
        </div>
      </header>

      <MyReservationsDialog
        open={showMyReservations}
        onClose={() => setShowMyReservations(false)}
      />

      <RecurringRulesDialog
        open={showRecurringRules}
        onClose={() => setShowRecurringRules(false)}
      />
    </>
  );
}
